import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp, 
  query, 
  where, 
  getDocs,
  addDoc
} from 'firebase/firestore';
import { User } from '../../models/types';
import { db } from '../config';
import { v4 as uuidv4 } from 'uuid';
import { crypto } from '../../utils/crypto';
import { getRoles, getRoleById } from './roleService';
import { Role } from '../../types/rbac';

// Collection reference
const usersRef = collection(db, 'users');

// Hash password with salt
const hashPassword = (password: string): {hashedPassword: string, salt: string} => {
  const salt = crypto.randomBytes(16).toString('hex');
  const hashedPassword = crypto
    .pbkdf2Sync(password, salt, 1000, 64, 'sha512')
    .toString('hex');
  return { hashedPassword, salt };
};

// Verify password against stored hash
const verifyPassword = (password: string, hash: string, salt: string): boolean => {
  const hashedPassword = crypto
    .pbkdf2Sync(password, salt, 1000, 64, 'sha512')
    .toString('hex');
  return hash === hashedPassword;
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const retryOperation = async <T>(
  operation: () => Promise<T>,
  retryCount = 0
): Promise<T> => {
  try {
    return await operation();
  } catch (error: any) {
    if (retryCount < MAX_RETRIES && 
        (error.code === 'unavailable' || 
         error.code === 'resource-exhausted' || 
         error.code === 'deadline-exceeded')) {
      console.log(`Retrying operation (attempt ${retryCount + 1})`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
      return retryOperation(operation, retryCount + 1);
    }
    throw error;
  }
};

// Create a new user
export const createUser = async (
  email: string, 
  password: string, 
  firstName: string, 
  lastName: string, 
  phone: string,
  roleId: string,
  passcode?: string
): Promise<User> => {
  try {
    // Check if user with this email already exists
    const existingUser = await getUserByEmail(email);
    if (existingUser) {
      throw new Error('User with this email already exists');
    }
    
    // Verify role exists
    const role = await getRoleById(roleId);
    if (!role) {
      throw new Error('Invalid role ID');
    }
    
    // Hash the password
    const { hashedPassword, salt } = hashPassword(password);
    
    // Generate unique ID
    const uid = uuidv4();
    
    // Prepare user document
    const now = new Date();
    const userData: User & { salt: string, passwordHash: string } = {
      id: uid,
      email,
      firstName,
      lastName,
      phone,
      roleId,
      password: '', // We don't store plain text password
      passwordHash: hashedPassword,
      salt,
      ...(passcode && { passcode }), // Only add passcode if provided
      createdAt: now,
      updatedAt: now
    };
    
    // Remove password field before storing
    const { password: _, ...userDataToStore } = userData;
    
    // Save to Firestore
    await setDoc(doc(db, 'users', uid), userDataToStore);
    
    // Return user data without sensitive fields
    const { passwordHash: __, salt: ___, ...safeUserData } = userData;
    return safeUserData;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Get user by email (for login)
export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return { ...userDoc.data(), id: userDoc.id } as User;
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<User | null> => {
  return retryOperation(async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = { ...userDoc.data(), id: userDoc.id } as User;
        
        // Get role data
        if (userData.roleId) {
          const role = await getRoleById(userData.roleId);
          if (role) {
            userData.role = role;
          }
        }
        
        return userData;
      }
      return null;
    } catch (error) {
      console.error('Error getting user by ID:', error);
      throw error;
    }
  });
};

// Update user
export const updateUser = async (userId: string, userData: Partial<User>): Promise<void> => {
  try {
    const userRef = doc(usersRef, userId);
    
    // If updating role, verify it exists
    if (userData.roleId) {
      const role = await getRoleById(userData.roleId);
      if (!role) {
        throw new Error('Invalid role ID');
      }
    }
    
    // If updating password, hash it first
    if (userData.password) {
      const { hashedPassword, salt } = hashPassword(userData.password);
      // Replace password with hash and salt
      const { password: _, ...rest } = userData;
      await updateDoc(userRef, {
        ...rest,
        passwordHash: hashedPassword,
        salt,
        updatedAt: new Date()
      });
    } else {
      await updateDoc(userRef, {
        ...userData,
        updatedAt: new Date()
      });
    }
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Sign in
export interface AuthResult {
  user: User;
  success: boolean;
  message?: string;
}

export const signIn = async (email: string, password: string): Promise<AuthResult> => {
  try {
    // Get user by email
    const user = await getUserByEmail(email);
    
    if (!user) {
      return { 
        success: false, 
        message: 'Invalid email or password',
        user: null as unknown as User 
      };
    }
    
    // Get full user data with password hash and salt
    const userDoc = await getDoc(doc(usersRef, user.id));
    const userData = userDoc.data();
    
    if (!userData || !userData.passwordHash || !userData.salt) {
      return { 
        success: false, 
        message: 'Invalid user data',
        user: null as unknown as User 
      };
    }
    
    // Verify password
    const isValid = verifyPassword(password, userData.passwordHash, userData.salt);
    
    if (!isValid) {
      return { 
        success: false, 
        message: 'Invalid email or password',
        user: null as unknown as User 
      };
    }
    
    // Get role data
    if (userData.roleId) {
      const role = await getRoleById(userData.roleId);
      if (role) {
        user.role = role;
      }
    }
    
    return { 
      success: true, 
      user 
    };
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

// Password reset
export const resetPassword = async (email: string): Promise<boolean> => {
  try {
    // Get user by email
    const user = await getUserByEmail(email);
    
    if (!user) {
      return false;
    }
    
    // Generate a temporary random password
    const tempPassword = Math.random().toString(36).slice(-8);
    
    // Update user with new password
    await updateUser(user.id, { password: tempPassword });
    
    // In a real app, you would send an email with password reset link
    console.log(`Password reset for ${email}. New temporary password: ${tempPassword}`);
    
    return true;
  } catch (error) {
    console.error('Error resetting password:', error);
    return false;
  }
};

// Get user by role
export const getUsersByRole = async (roleId: string): Promise<User[]> => {
  try {
    const q = query(usersRef, where('roleId', '==', roleId));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Filter out sensitive fields
      const { passwordHash, salt, ...userData } = data;
      return { ...userData, id: doc.id } as User;
    });
  } catch (error) {
    console.error('Error getting users by role:', error);
    throw error;
  }
};

// Get staff members (includes both staff and admin roles)
export const getStaffMembers = async (): Promise<User[]> => {
  try {
    // First get all roles
    const roles = await getRoles();
    
    // Find staff and admin role IDs
    const staffRole = roles.find(r => r.name.toLowerCase() === 'staff');
    const adminRole = roles.find(r => r.name.toLowerCase() === 'admin');
    
    if (!staffRole && !adminRole) {
      console.warn('No staff or admin roles found');
      return [];
    }
    
    // Get users with either role
    const roleIds = [staffRole?.id, adminRole?.id].filter(Boolean);
    const q = query(usersRef, where('roleId', 'in', roleIds));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Filter out sensitive fields
      const { passwordHash, salt, ...userData } = data;
      return { ...userData, id: doc.id } as User;
    });
  } catch (error) {
    console.error('Error getting staff members:', error);
    throw error;
  }
};

// Verify user passcode for launch ramp access
export const verifyUserPasscode = async (userId: string, passcode: string): Promise<boolean> => {
  try {
    const user = await getUserById(userId);
    
    console.log("Passcode verification attempt:", {
      providedPasscode: passcode,
      storedPasscode: user?.passcode,
      match: user?.passcode === passcode,
      userId: userId,
      userExists: !!user
    });
    
    if (!user || !user.passcode) {
      console.log("User not found or no passcode set");
      return false;
    }
    
    // Check for type issues by converting both to strings
    const isMatch = String(user.passcode) === String(passcode);
    console.log("Passcode comparison:", { 
      providedType: typeof passcode,
      storedType: typeof user.passcode,
      isMatch: isMatch
    });
    
    return isMatch;
  } catch (error) {
    console.error('Error verifying passcode:', error);
    return false;
  }
};

// Verify passcode by email (for login flow)
export const verifyPasscodeByEmail = async (email: string, passcode: string): Promise<{isValid: boolean, userId?: string}> => {
  return retryOperation(async () => {
    try {
      console.log('Verifying passcode by email:', email);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        console.log('No user found with this email');
        return { isValid: false };
      }
      
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      if (userData.passcode === passcode) {
        console.log('Passcode verified successfully');
        return { isValid: true, userId: userDoc.id };
      }
      
      return { isValid: false };
    } catch (error) {
      console.error('Error verifying passcode by email:', error);
      throw error;
    }
  });
};

// Verify passcode directly (for direct staff access)
export const verifyPasscodeDirectly = async (passcode: string): Promise<{isValid: boolean, userId?: string}> => {
  return retryOperation(async () => {
    try {
      // Only log the passcode being checked and the result
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('passcode', '==', String(passcode)));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('No user found with this passcode:', passcode);
        }
        return { isValid: false };
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      if (String(userData.passcode) === String(passcode)) {
        if (process.env.NODE_ENV === 'development') {
          console.info('Passcode verified for user:', userDoc.id);
        }
        return { isValid: true, userId: userDoc.id };
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('Passcode did not match after string conversion for user:', userDoc.id);
      }
      return { isValid: false };
    } catch (error) {
      console.error('Error verifying passcode:', error);
      throw error;
    }
  });
}; 